import {Flex, Form, Input, InputNumber, Radio, Select, Spin, Switch} from 'antd';
import FormItem from 'antd/es/form/FormItem';
import {
    AdGroupBean,
    AgencyBean,
    CampaignBean,
    CreativeListItemBean,
    ItemResponseAdGroupBeanObject
} from 'common/api/api';
import {adGroupApi, campaignApi, creativeApi, dictionaryApi} from 'common/api/entitiesApi';
import {AnchorsForm} from 'common/components/AnchorsForm';
import {ButtonsPanel} from 'common/components/ButtonsPanel';
import {CardTitleWithTooltip} from 'common/components/CardTitleWithTooltip';
import {DealIdSelector} from 'common/components/DealIdSelector';
import {BrandSafetySelector} from 'common/components/dictionary/BrandSafetySelector';
import {BrowserSelector} from 'common/components/dictionary/BrowserSelector';
import {ConnectionTypeSelector} from 'common/components/dictionary/ConnectionTypeSelector.tsx';
import {CountrySelector} from 'common/components/dictionary/CountrySelector';
import {DeviceTypeSelector} from 'common/components/dictionary/DeviceTypeSelector';
import {KeywordSelector} from 'common/components/dictionary/KeywordSelector';
import {LanguageSelector} from 'common/components/dictionary/LanguageSelector';
import {OsSelector} from 'common/components/dictionary/OsSelector';
import {TimeZoneSelector} from 'common/components/dictionary/TimeZoneSelector';
import {FormCard} from 'common/components/FormCard';
import {IABCategoriesSelector} from 'common/components/IABCategoriesSelector';
import {AdUnitSelector} from 'common/components/reloadable/AdUnitSelector';
import {AdvertiserSelector} from 'common/components/reloadable/AdvertiserSelector';
import {CitySelector} from 'common/components/reloadable/CitySelector';
import {IspOrganizationSelector} from 'common/components/reloadable/IspOrganizationSelector';
import {RegionSelector} from 'common/components/reloadable/RegionSelector';
import {RetargetingListSelector} from 'common/components/reloadable/RetargetingListSelector';
import {SspSelector} from 'common/components/reloadable/SspSelector';
import {StartEndDates} from 'common/components/StartEndDates';
import {StatusSwitch} from 'common/components/StatusSwitch';
import {useAppContext} from 'common/context/AppContext';
import {CommonRoutes} from 'common/context/commonRoutes';
import {useUserContext} from 'common/context/UserContext';
import {queueListRepositionOnModifiedChange} from 'common/util/listNavigation.ts'
import {apiCall, FormMode, FormProps, getCopyName, showError, useDebounce} from 'common/util/Util'
import dayjs from 'dayjs'
import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import BudgetCard from './BudgetCard';
import {CampaignSelectPanel} from './CampaignSelectPanel';
import {validateCpcCreativeSelection} from './creativeGroups';
import CreativesCard from './CreativesCard';
import DayPartingCard from './day-parting/DayPartingCard';
import {ALL_HOURS} from './day-parting/DayPartingSelector';
import FilterListsCard from './FilterListsCard';
import BudgetTypeEnum = AdGroupBean.BudgetTypeEnum
import DealIdListModeEnum = AdGroupBean.DealIdListModeEnum
import DeliveryTypeEnum = AdGroupBean.DeliveryTypeEnum
import MediaTypeEnum = AdGroupBean.MediaTypeEnum
import OptimizationTypeEnum = AdGroupBean.OptimizationTypeEnum
import PricingTypeEnum = AdGroupBean.PricingTypeEnum
import PriorityEnum = AdGroupBean.PriorityEnum

export const AdGroupForm: React.FC<FormProps> = ({mode, agency}) => {
    const navigateTo = useNavigate()
    const appContext = useAppContext()

    const {id} = useParams<{ id?: string }>()
    const idNum = id ? parseInt(id) : 0

    const {user, featureAccess, hasLinkedSystemSsps} = useUserContext()
    const advertiserId = user?.advertiserId
    const hasFeature = (feature: AgencyBean.FeatureAccessEnum) => featureAccess?.includes(feature) ?? false
    const canLinkAdUnits = hasFeature(AgencyBean.FeatureAccessEnum.Publisher)
    const canLinkSsps = hasFeature(AgencyBean.FeatureAccessEnum.SSP) || hasLinkedSystemSsps
    const showInventoryCard = canLinkAdUnits || canLinkSsps
    const initialEntity = {
        advertiserId: agency ? undefined : advertiserId,
        active: true,
        dayParting: ALL_HOURS,
        budgetType: BudgetTypeEnum.Total,
        deliveryType: DeliveryTypeEnum.Distributed,
        pricingType: PricingTypeEnum.CPM,
        priority: PriorityEnum.Medium,
        mediaType: AdGroupBean.MediaTypeEnum.All,
        dealIdListMode: DealIdListModeEnum.Black,
        optimizationType: OptimizationTypeEnum.None,
        coppa: AdGroupBean.CoppaEnum.Include,
        ...(mode === FormMode.create && {startDate: dayjs().format('YYYY-MM-DD')})
    } as AdGroupBean

    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [entity, setEntity] = useState<AdGroupBean>(initialEntity)
    const campaignId = Form.useWatch('campaignId', form)
    const pricingType = Form.useWatch('pricingType', form) as PricingTypeEnum | undefined
    const [campaign, setCampaign] = useState<CampaignBean>()

    const [kwList, setKwList] = useState<string[]>([])
    const [kwSearch, setKwSearch] = useState<string>('')
    const debouncedKwSearch = useDebounce(kwSearch, 500)

    const prevPricingTypeRef = useRef<PricingTypeEnum | undefined>(undefined)

    const {
        setParentAdvertiserId,
        backToAdGroupId,
        setBackToAdGroupId,
        setBackToAdGroupMode,
        creativeIdToLink,
        setCreativeIdToLink,
        campaignIdToLink,
        setCampaignIdToLink
    } = useAppContext()

    useEffect(() => {
        if (mode == 'create' && campaignId !== undefined) {
            apiCall(
                setLoading,
                appContext,
                () => campaignApi.campaign(campaignId),
                (data: any) => {
                    if (data && data.data) {
                        form.setFieldValue('timezone', data.data.timezone)
                        setEntity({...entity, startDate: data.data.startDate, endDate: data.data.endDate})
                    }
                }
            )
        }
    }, [campaignId])

    useEffect(() => {
        const prev = prevPricingTypeRef.current
        const curr = pricingType
        prevPricingTypeRef.current = curr

        if (prev && prev !== PricingTypeEnum.CPC && curr === PricingTypeEnum.CPC) {
            if (entity.creativeIds && entity.creativeIds.length > 0) {
                setEntity((p) => ({...p, creativeIds: []}))
            }
        }
    }, [pricingType])

    useEffect(() => {
        if (pricingType !== PricingTypeEnum.CPC || !entity.creativeIds || entity.creativeIds.length === 0) {
            return
        }

        let cancelled = false

        const normalizeCpcCreativeIds = async () => {
            try {
                const response = await creativeApi.creativeList(
                    'name',
                    'asc',
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    entity.creativeIds
                )

                if (cancelled) return

                const creatives = response.data?.map((item) => item.data || ({} as CreativeListItemBean)) || []
                const {invalidCreativeIds, firstError} = validateCpcCreativeSelection(
                    creatives,
                    restoredCreativeGroupsSnapshot(entity)
                )

                if (invalidCreativeIds.length === 0) return
                const invalidCreativeIdsSet = new Set(invalidCreativeIds)

                setEntity((prev) => {
                    const currentIds = prev.creativeIds || []
                    const filteredCreativeIds = currentIds.filter((id) => !invalidCreativeIdsSet.has(id))

                    if (filteredCreativeIds.length === currentIds.length) {
                        return prev
                    }

                    return {
                        ...prev,
                        creativeIds: filteredCreativeIds
                    }
                })

                if (firstError) {
                    showError(appContext, firstError)
                }
            } catch (e) {
                if (!cancelled) {
                    showError(appContext, e)
                }
            }
        }

        void normalizeCpcCreativeIds()

        return () => {
            cancelled = true
        }
    }, [appContext, entity.creativeIds, pricingType])

    const handleSubmit = () => {
        setLoading(true)
        if (mode === FormMode.create || mode === FormMode.copy) {
            form.validateFields().then((formData) =>
                apiCall(
                    setLoading,
                    appContext,
                    () => adGroupApi.createAdGroup({...entity, ...formData}),
                    () => navigateTo(CommonRoutes.AD_GROUP)
                )
            )
        } else {
            form.validateFields().then((formData) =>
                apiCall(
                    setLoading,
                    appContext,
                    () => adGroupApi.editAdGroup({...entity, ...formData}, idNum),
                    (response) => {
                        queueListRepositionOnModifiedChange({
                            appContext,
                            routeKey: CommonRoutes.AD_GROUP,
                            previousEntity: entity,
                            responseEntity: response?.data
                        })

                        navigateTo(CommonRoutes.AD_GROUP)
                    }
                )
            )
        }
    }

    const saveAdGroupState = (extraState: Record<string, unknown> = {}) => {
        const formValues = form.getFieldsValue()
        localStorage.setItem(`adGroup${id}State`, JSON.stringify({...entity, ...formValues, ...extraState}))
    }

    const onError = (e) => {
        setLoading(false)
        showError(appContext, e)
    }

    function onCancel() {
        navigateTo(CommonRoutes.AD_GROUP)
    }

    useEffect(() => {
        dictionaryApi
            .keywords(kwSearch)
            .then(setKwList)
    }, [debouncedKwSearch])

    useEffect(() => {
        if (!agency && advertiserId && !entity.advertiserId) {
            setEntity((prev) => ({...prev, advertiserId: advertiserId}))
        }
    }, [agency, advertiserId, entity.advertiserId])

    useEffect(() => {
        // load from local storage if it was tmp switch to campaign or creative
        if (creativeIdToLink !== undefined || campaignIdToLink !== undefined || backToAdGroupId !== undefined) {
            const savedState = localStorage.getItem(`adGroup${id}State`)
            const restored = savedState ? JSON.parse(savedState) : null
            if (restored) {
                const nextCreativeIds = creativeIdToLink == undefined
                    ? restored.creativeIds
                    : restored.creativeIds?.includes(creativeIdToLink)
                        ? restored.creativeIds
                        : [...(restored.creativeIds || []), creativeIdToLink]

                const restoredEntity = {
                    ...restored,
                    creativeIds: nextCreativeIds,
                    campaignId: campaignIdToLink === undefined
                        ? restored.campaignId
                        : campaignIdToLink === 0
                            ? undefined
                            : campaignIdToLink
                }

                form.setFieldsValue({...restoredEntity})
                setEntity(restoredEntity)

                if (campaignIdToLink != undefined) {
                    form.setFieldsValue({campaignId: campaignIdToLink === 0 ? undefined : campaignIdToLink})
                }
            }
        } else if (mode === FormMode.edit || mode === FormMode.copy) {
            // load from Backend
            setLoading(true)
            adGroupApi
                .adGroup(idNum)
                .then((adGroupBeanObject: ItemResponseAdGroupBeanObject) => {
                    setLoading(false)
                    if (adGroupBeanObject.data) {
                        const entityData =
                            mode === FormMode.copy
                                ? {...adGroupBeanObject.data, name: getCopyName(adGroupBeanObject.data.name)}
                                : {...adGroupBeanObject.data}
                        form.setFieldsValue(entityData)
                        setEntity(entityData)
                        prevPricingTypeRef.current = (adGroupBeanObject.data.pricingType as any) ?? prevPricingTypeRef.current
                    } else {
                        showError(appContext, "Can't load data")
                    }
                })
                .catch(onError)
        } else {
            form.setFieldsValue({...entity})
            prevPricingTypeRef.current = entity.pricingType as any
        }

        localStorage.removeItem(`adGroup${id}State`)
        setBackToAdGroupId(undefined)
        setCreativeIdToLink(undefined)
        setCampaignIdToLink(undefined)
    }, [])

    function restoredCreativeGroupsSnapshot(currentEntity: AdGroupBean): Record<number, 'Banner' | 'Video' | 'Native' | null> | undefined {
        const snapshot = (currentEntity as any).creativeGroupsSnapshot
        return snapshot && typeof snapshot === 'object' ? snapshot : undefined
    }

    useEffect(() => {
        if (campaignId) {
            apiCall(
                setLoading,
                appContext,
                () => campaignApi.campaign(campaignId),
                (res: any) => {
                    if (res.data) {
                        setCampaign(res.data as CampaignBean)
                    }
                }
            )
        }
    }, [campaignId])

    const deleteAdGroup = () => {
        apiCall(
            setLoading,
            appContext,
            () => adGroupApi.deleteAdGroup(idNum),
            () => navigateTo(CommonRoutes.AD_GROUP)
        )
    }

    function openCampaign(path) {
        saveAdGroupState()
        setParentAdvertiserId(entity.advertiserId)
        setBackToAdGroupId(idNum)
        setBackToAdGroupMode(mode)
        navigateTo(path)
    }

    return (
        <Spin spinning={loading}>
            <AnchorsForm form={form} onFinish={handleSubmit} onCancel={onCancel} showAnchors>
                <FormCard
                    id="general"
                    title="General"
                    extra={<StatusSwitch entity={entity} setEntity={setEntity}/>}
                    styles={{header: {borderBottom: 'none'}}}
                >
                    {agency && (
                        <FormItem
                            required={true}
                            label="Advertiser"
                            tooltip="Select the advertiser who runs this campaign."
                            rules={[{required: true, message: 'Advertiser is required'}]}
                        >
                            <AdvertiserSelector
                                value={entity.advertiserId}
                                disabled={mode === 'edit'}
                                onChange={(advertiserId: number) =>
                                    setEntity({
                                        ...entity,
                                        advertiserId: advertiserId || 0
                                    })
                                }
                            />
                        </FormItem>
                    )}

                    <FormItem
                        required
                        label="Campaign"
                        tooltip="Choose an existing campaign from the list or create a new one."
                        name="campaignId"
                        rules={[{required: true, message: 'Campaign is required'}]}
                    >
                        <CampaignSelectPanel
                            disabled={mode === 'edit'}
                            advertiserId={entity.advertiserId}
                            onEdit={() => openCampaign(`/campaign/${campaignId}/edit`)}
                            onNew={() => openCampaign(CommonRoutes.CAMPAIGN_CREATE)}
                        />
                    </FormItem>

                    <FormItem
                        required
                        name="name"
                        label="Name"
                        rules={[{required: true, message: 'Please input AdGroup Name'}]}
                    >
                        <Input/>
                    </FormItem>

                    <FormItem
                        name="iabCategoriesIds"
                        label="Categories"
                        tooltip="Select the categories that match the content or your advertising."
                    >
                        <IABCategoriesSelector/>
                    </FormItem>

                    <FormItem
                        name="timezone"
                        label="Time Zone"
                        required
                        rules={[{required: true, message: 'Please specify Time Zone'}]}
                    >
                        <TimeZoneSelector disabled={mode === 'edit' || campaignId === undefined}/>
                    </FormItem>

                    <StartEndDates
                        entity={entity}
                        setEntity={(e) => setEntity(e)}
                        form={form}
                        lookupEntity={campaign}
                        lookupMessage="The chosen date range is out of bounds of campaign's date range."
                    />

                    <FormItem
                        name="priority"
                        label="Priority"
                        required
                        rules={[{required: true, message: 'Please select Priority'}]}
                    >
                        <Select
                            options={Object.values(PriorityEnum).map((priority) => ({
                                label: priority,
                                value: priority
                            }))}
                        />
                    </FormItem>
                </FormCard>

                <BudgetCard id="budget" title="Budget" form={form}/>

                <FormCard id="creatives" title="Creatives">
                    <CreativesCard
                        entity={entity}
                        setEntity={setEntity}
                        saveAdGroupState={saveAdGroupState}
                        adGroupId={entity.id ?? 0}
                        mode={mode}
                        pricingType={pricingType}
                    />
                </FormCard>

                {agency && showInventoryCard && (
                    <FormCard id="inventory" title="Inventory">
                        {canLinkSsps && (
                            <FormItem
                                name="sspIds"
                                label="Linked SSPs"
                                tooltip="Choose the supply-side platforms (SSPs) providing inventory for your ads."
                            >
                                <SspSelector multiple placeholder="All available SSPs"/>
                            </FormItem>
                        )}

                        {canLinkAdUnits && (
                            <FormItem
                                name="adUnitIds"
                                label="Linked Ad Units"
                                tooltip="Select the specific ad units or placements where the campaign will appear."
                            >
                                <AdUnitSelector multiple placeholder="All available AdUnits"/>
                            </FormItem>
                        )}

                        {canLinkSsps && (
                            <FormItem
                                name="dealIdList"
                                label="Deal IDs"
                                tooltip="Apply specific private marketplace deals or programmatic guaranteed deals to this campaign."
                            >
                                <DealIdSelector placeholder="Select Deal IDs"/>
                            </FormItem>
                        )}
                    </FormCard>
                )}

                <DayPartingCard
                    id="dayParting"
                    title={
                        <CardTitleWithTooltip
                            title="Day Parting"
                            tooltip="Specify weekdays and hours when your ads are allowed to run."
                        />
                    }
                    dayParting={entity.dayParting || ALL_HOURS}
                    setDayParting={(value) => setEntity({...entity, dayParting: value})}
                />

                <FormCard
                    id="frequencyCapping"
                    title={
                        <CardTitleWithTooltip
                            title="Frequency Capping"
                            tooltip="Set limits for the number of times a user sees your ad while ad group is running."
                        />
                    }
                >
                    <FormItem label="Total">
                        <Flex gap={16}>
                            <InputNumber
                                style={{width: 200}}
                                precision={0}
                                min={1}
                                value={entity.totalFC}
                                onChange={(value) => setEntity({...entity, totalFC: value || undefined})}
                            />
                            <span style={{display: 'inline-flex', alignItems: 'center'}}>Daily</span>
                            <InputNumber
                                style={{width: 200}}
                                precision={0}
                                min={1}
                                value={entity.dailyFC}
                                onChange={(value) => setEntity({...entity, dailyFC: value || undefined})}
                            />
                        </Flex>
                    </FormItem>
                </FormCard>

                <FormCard id="targeting" title="Targeting">
                    <FormItem
                        name="mediaType"
                        label="Media Type"
                        tooltip="Choose the type of media where your ads will appear — websites, mobile apps, or both."
                        rules={[{required: true, message: 'Please input Media Type'}]}
                    >
                        <Radio.Group>
                            {Object.values(MediaTypeEnum).map((type) => (
                                <Radio key={type} value={type}>
                                    {type === MediaTypeEnum.All ? 'All Types' : type}
                                </Radio>
                            ))}
                        </Radio.Group>
                    </FormItem>

                    <FormItem
                        label="Content Categories"
                        name="publisherIabCategoriesIds"
                        tooltip={
                            <>
                                <div>These categories refer to the media where your ads will be displayed. The
                                    categories are provided by the media.
                                </div>
                                <div style={{marginTop: 8}}>
                                    Warning. Please note that not all media provide categories, which may affect ad
                                    performance. If no category is provided, your ads may not be shown.
                                </div>
                            </>
                        }
                    >
                        <IABCategoriesSelector placeholder="Any Category"/>
                    </FormItem>

                    <FormItem
                        name="userLanguages"
                        label="User Language"
                        tooltip="Select language. This is the language set in the user’s browser settings."
                    >
                        <LanguageSelector placeholder="Any Language" multiple/>
                    </FormItem>

                    <FormItem
                        name="deviceTypes"
                        label="Device Types"
                        tooltip="Select the device types where your ads will be shown, such as desktop, mobile, or tablet."
                    >
                        <DeviceTypeSelector placeholder="Any Device Type"/>
                    </FormItem>

                    <FormItem
                        name="operatingSystems"
                        label="Operating Systems"
                        tooltip="Select operating systems and versions for mobile OS. This allows you to show ads only to users on specific platforms and OS versions."
                    >
                        <OsSelector placeholder="Any Operating System"/>
                    </FormItem>

                    <FormItem
                        name="browsers"
                        label="Browsers"
                        tooltip="Select the browsers you want to target with your ads. Ads will be delivered only to users using the selected browsers."
                    >
                        <BrowserSelector placeholder="Any Browser"/>
                    </FormItem>

                    <FormItem
                        name="connectionTypes"
                        label="Connection Type"
                        tooltip="Select the type of internet connection to target users based on how they access the internet (Wi-Fi, cellular, Ethernet, etc.)."
                    >
                        <ConnectionTypeSelector placeholder="Any Connection Type"/>
                    </FormItem>

                    <FormItem
                        label="ISP / Organization"
                        tooltip="Target users by the organization that owns their IP address — mobile carriers, internet providers, or companies with their own networks."
                    >
                        <IspOrganizationSelector
                            value={entity.companyNames}
                            placeholder="Any ISP / Organization"
                            onChange={(companyNames) => setEntity({...entity, companyNames})}
                        />
                    </FormItem>

                    <FormItem
                        name="coppa"
                        label="COPPA Traffic"
                        tooltip="Filters bid requests by the COPPA flag (regs.coppa = 1), which marks traffic from child-directed apps and sites."
                    >
                        <Radio.Group>
                            <Radio value={AdGroupBean.CoppaEnum.Include}>Include Child-Directed</Radio>
                            <Radio value={AdGroupBean.CoppaEnum.Exclude}>Exclude Child-Directed</Radio>
                            <Radio value={AdGroupBean.CoppaEnum.Only}>Child-Directed Only</Radio>
                        </Radio.Group>
                    </FormItem>
                </FormCard>

                <FormCard id="contextual" title="Contextual Targeting">
                    <FormItem
                        name="keywords"
                        label="Keywords"
                    >
                        <KeywordSelector placeholder="Search keywords" mode="multiple" kwList={kwList}
                                         setKwSearch={setKwSearch}/>
                    </FormItem>
                    <FormItem
                        name="brandSafety"
                        label="Brand Safety"
                    >
                        <BrandSafetySelector placeholder="Select Brand Safety category"/>
                    </FormItem>
                </FormCard>

                <FormCard id="geoTargeting" title="Geo Targeting">
                    <p style={{marginTop: -18, marginBottom: 24, maxWidth: 1200, fontSize: 14, lineHeight: 1.45}}>
                        We support geo-targeting, which allows you to select specific countries, regions, and cities.
                        This helps
                        you show your ads only to users in the locations you choose. You can select different countries,
                        regions,
                        and cities independently.
                    </p>
                    <FormItem
                        name="countriesIds"
                        label="Countries"
                        required={pricingType === PricingTypeEnum.CPC}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (pricingType !== PricingTypeEnum.CPC) return Promise.resolve()
                                    const arr = Array.isArray(value) ? value : []
                                    if (arr.length === 0) return Promise.reject(new Error('For CPC, at least 1 country is required'))
                                    if (arr.length > 5) return Promise.reject(new Error('For CPC, maximum 5 countries are allowed'))
                                    return Promise.resolve()
                                }
                            }
                        ]}
                    >
                        <CountrySelector placeholder="Any Country"/>
                    </FormItem>

                    <FormItem label="Regions">
                        <RegionSelector
                            value={entity.regionIds}
                            placeholder="Any Region"
                            onChange={(regionIds) => setEntity({...entity, regionIds})}
                        />
                    </FormItem>

                    <FormItem label="Cities">
                        <CitySelector
                            value={entity.cityIds}
                            placeholder="Any City"
                            onChange={(cityIds) => setEntity({...entity, cityIds})}
                        />
                    </FormItem>
                </FormCard>

                <FormCard id="retargetingLists" title="Retargeting Lists">
                    <FormItem label="Retargeting Lists" name="retargetingListIds">
                        <RetargetingListSelector
                            multiple
                            excludeFailed
                            advertiserId={entity.advertiserId}
                            value={entity.retargetingListIds}
                            placeholder="Select Retargeting Lists"
                            onChange={(retargetingListIds) =>
                                setEntity({
                                    ...entity,
                                    retargetingListIds: (retargetingListIds as number[] | undefined) || []
                                })
                            }
                        />
                    </FormItem>
                </FormCard>

                <FormCard
                    id="filtering"
                    title="Filter Lists"
                >
                    <FilterListsCard entity={entity} setEntity={setEntity}/>
                </FormCard>

                <FormCard id="antifraud" title="Antifraud">
                    <FormItem name="adFraudStatus" label="Active" valuePropName="checked" initialValue={false}>
                        <Switch size="small"/>
                    </FormItem>
                </FormCard>

                <ButtonsPanel onCancel={onCancel} showDelete={mode === FormMode.edit} onDelete={deleteAdGroup}/>
            </AnchorsForm>
        </Spin>
    )
}
